/**
 * Application Entry Point
 * AIDLC Administrative Backend
 *
 * Initializes:
 * 1. MSAL PublicClientApplication instance (production)
 * 2. Dev auth bypass mode (development without Azure)
 * 3. React 18 createRoot rendering
 */

import { StrictMode, useState } from 'react';
import { createRoot } from 'react-dom/client';
import { App } from './App';
import { LoginPage } from './components/auth/LoginPage';
import './styles/globals.css';

const DEV_AUTH_BYPASS = import.meta.env.VITE_DEV_AUTH_BYPASS === 'true';

// Register service worker (runs immediately, no auth needed)
if ('serviceWorker' in navigator) {
  window.addEventListener('load', () => {
    navigator.serviceWorker.register('/sw.js').catch(err => {
      console.warn('[SW] Registration failed:', err);
    });
  });
}

/**
 * Mount the application
 */
const rootElement = document.getElementById('root');
if (!rootElement) {
  throw new Error('Root element not found. Check public/index.html.');
}

if (DEV_AUTH_BYPASS) {
  /**
   * DEV MODE: Bypass MSAL entirely.
   * Shows the LoginPage for visual testing, then enters the app on "sign in".
   * useAuth() returns a mock admin user once past the login page.
   * NEVER ship this to production.
   */
  console.warn('[Auth] DEV BYPASS ACTIVE -- MSAL authentication is disabled. Do NOT use in production.');

  import('@azure/msal-browser').then(() => {
    import('@azure/msal-react').then(({ MsalProvider }) => {
      import('./msalInstance').then(async ({ ensureMsalInitialized }) => {
        const msalInstance = await ensureMsalInitialized();

        function DevRoot() {
          const [authenticated, setAuthenticated] = useState(false);

          if (!authenticated) {
            return <LoginPage onLogin={() => setAuthenticated(true)} isLoading={false} />;
          }

          return (
            <MsalProvider instance={msalInstance}>
              <App />
            </MsalProvider>
          );
        }

        createRoot(rootElement).render(
          <StrictMode>
            <DevRoot />
          </StrictMode>
        );
      });
    });
  });
} else {
  /**
   * PRODUCTION MODE: Full MSAL authentication
   */
  import('@azure/msal-browser').then(({ EventType, InteractionStatus }) => {
    import('@azure/msal-react').then(({ MsalProvider, AuthenticatedTemplate, UnauthenticatedTemplate, useMsal }) => {
      import('./authConfig').then(async ({ loginRequest }) => {
        const { ensureMsalInitialized } = await import('./msalInstance');
        const msalInstance = await ensureMsalInitialized();

        // Handle redirect response (processes auth code after redirect flow returns)
        const redirectResponse = await msalInstance.handleRedirectPromise();
        if (redirectResponse) {
          msalInstance.setActiveAccount(redirectResponse.account);
        }

        msalInstance.addEventCallback((event) => {
          if (event.eventType === EventType.LOGIN_SUCCESS && event.payload) {
            const result = event.payload as import('@azure/msal-browser').AuthenticationResult;
            msalInstance.setActiveAccount(result.account);
          }
        });

        // Set active account if one already exists from a previous session
        const accounts = msalInstance.getAllAccounts();
        if (accounts.length > 0 && !msalInstance.getActiveAccount()) {
          msalInstance.setActiveAccount(accounts[0]);
        }

        function LoginScreen() {
          const { inProgress } = useMsal();

          const handleLogin = () => {
            msalInstance.loginRedirect(loginRequest);
          };

          return (
            <LoginPage
              onLogin={handleLogin}
              isLoading={inProgress !== InteractionStatus.None}
            />
          );
        }

        function AuthenticatedApp() {
          // After auth, navigate away from /auth so React Router sees real routes
          if (window.location.pathname === '/auth') {
            window.history.replaceState({}, '', '/');
          }
          return <App />;
        }

        createRoot(rootElement).render(
          <StrictMode>
            <MsalProvider instance={msalInstance}>
              <AuthenticatedTemplate>
                <AuthenticatedApp />
              </AuthenticatedTemplate>
              <UnauthenticatedTemplate>
                <LoginScreen />
              </UnauthenticatedTemplate>
            </MsalProvider>
          </StrictMode>
        );
      });
    });
  });
}
